import React from "react"
import ReactDOM from "react-dom/client"
import { AppState, Auth0Provider } from "@auth0/auth0-react"
import { I18nextProvider } from "react-i18next"
import "./index.css"
import { QueryClientProvider } from "@tanstack/react-query"
import {
	auth0Audience,
	auth0ClientId,
	auth0Domain,
	photoEditorMaintenanceStatusUrl,
	queryClient,
} from "./api/constants"
import { refreshUserAndGet } from "@/api/users-api"
import { ErrorBoundary } from "@sentry/react"
import { RootErrorView } from "@/components/error-views/RootErrorView"
import { queryClientAtom } from "jotai-tanstack-query"
import { useHydrateAtoms } from "jotai/utils"
import { analytics, prepareAmplitudeStorage } from "@/telemetry/amplitude"
import { BrowserCompatibilityGuard } from "@/components/BrowserCompatibilityGuard"
import { RouterProvider } from "@tanstack/react-router"
import { router } from "./router"
import { getRedirectURI } from "@/utils/auth"
import { sentryInit } from "@/telemetry/sentry"
import {
	getPixiesetUpgradeTabId,
	getUpgradeLocalStorageItem,
	PIXIESET_UPGRADE_ACK_KEY,
	PIXIESET_UPGRADE_COMPLETE_KEY,
	PIXIESET_UPGRADE_RETURN_PARAM,
	PIXIESET_UPGRADE_RETURN_TO_KEY,
	removeUpgradeLocalStorageItem,
	setUpgradeLocalStorageItem,
} from "@/utils/pixieset-upgrade"
import { applyThemeMode, getStoredThemeMode } from "@/utils/theme-mode"
import { trackSubscriptionSucceededIfNeeded } from "@/telemetry/subscription-succeeded"
import { MaintenanceModeGate } from "@/components/MaintenanceModeGate"
import { applyMaintenanceBypassFromUrl } from "@/utils/measurement/feature-flag"
import { isElectronApp } from "@/utils/electron"
import i18n, { initI18n } from "@/i18n"
import { loadAppConfigForLaunch } from "@/api/app-config-api"
import { getEnabledLocales } from "@/utils/localization-access"

const PIXIESET_UPGRADE_RETURN_TO_TTL_MS = 10 * 60 * 1000

const refreshUserAndTrackSubscriptionSucceeded = () => {
	return refreshUserAndGet().then((user) => trackSubscriptionSucceededIfNeeded(user))
}

// When the upgrade flow returns to this tab, broadcast completion and let the origin tab take over.
const handlePixiesetUpgradeReturn = () => {
	const searchParams = new URLSearchParams(window.location.search)
	if (searchParams.get("from") !== PIXIESET_UPGRADE_RETURN_PARAM) {
		return
	}

	// Remove the marker param so the user doesn't keep re-triggering the return handler.
	const cleanedUrl = new URL(window.location.href)
	cleanedUrl.searchParams.delete("from")

	// Recover the originating tab id + return target (if we have one).
	let returnTo = cleanedUrl.pathname + cleanedUrl.search + cleanedUrl.hash
	let targetTabId: string | null = null
	const rawContext = getUpgradeLocalStorageItem(PIXIESET_UPGRADE_RETURN_TO_KEY)

	if (rawContext) {
		try {
			const parsed = JSON.parse(rawContext) as {
				returnTo?: string
				tabId?: string
				timestamp?: number
			}
			if (parsed && typeof parsed === "object") {
				const isFresh =
					typeof parsed.timestamp === "number" &&
					Date.now() - parsed.timestamp < PIXIESET_UPGRADE_RETURN_TO_TTL_MS
				if (typeof parsed.returnTo === "string" && isFresh) {
					returnTo = parsed.returnTo
				}
				if (typeof parsed.tabId === "string" && isFresh) {
					targetTabId = parsed.tabId
				}
			}
		} catch {
			// If the stored value is legacy/plain, use it once as a best-effort path.
			// Invalid values will be ignored later when parsing into a URL.
			returnTo = rawContext
		}
	}
	removeUpgradeLocalStorageItem(PIXIESET_UPGRADE_RETURN_TO_KEY)

	// Notify other tabs; only the tab with the matching id will act on it.
	setUpgradeLocalStorageItem(
		PIXIESET_UPGRADE_COMPLETE_KEY,
		JSON.stringify({
			returnTo,
			tabId: targetTabId,
			timestamp: Date.now(),
		}),
	)

	window.history.replaceState({}, document.title, cleanedUrl)
	if (!targetTabId) {
		// No originating tab known; keep this tab open and refresh user data here.
		void refreshUserAndTrackSubscriptionSucceeded()
		return
	}

	// Wait for an ACK from the originating tab before closing this return tab.
	const ackListener = (event: StorageEvent) => {
		if (event.key !== PIXIESET_UPGRADE_ACK_KEY || !event.newValue) {
			return
		}

		try {
			const payload = JSON.parse(event.newValue) as { tabId?: string }
			if (payload.tabId !== targetTabId) {
				return
			}
			window.removeEventListener("storage", ackListener)
			window.close()
		} catch {
			// ignore malformed payloads
		}
	}

	window.addEventListener("storage", ackListener)
	window.setTimeout(() => {
		window.removeEventListener("storage", ackListener)
	}, 2000)
}

// Listen for upgrade completion and only react if it's addressed to this tab.
const listenForPixiesetUpgradeReturn = () => {
	const currentTabId = getPixiesetUpgradeTabId()
	window.addEventListener("storage", (event) => {
		if (event.key !== PIXIESET_UPGRADE_COMPLETE_KEY || !event.newValue) {
			return
		}

		try {
			const payload = JSON.parse(event.newValue) as { returnTo?: string; tabId?: string }
			if (!payload.tabId || payload.tabId !== currentTabId) {
				return
			}

			// ACK so the return tab can safely close without orphaning the user.
			setUpgradeLocalStorageItem(
				PIXIESET_UPGRADE_ACK_KEY,
				JSON.stringify({ tabId: currentTabId, timestamp: Date.now() }),
			)

			const returnToUrl = new URL(payload.returnTo ?? "/", window.location.origin)
			const target = returnToUrl.pathname + returnToUrl.search + returnToUrl.hash
			router.history.replace(target)
			// Ensure subscription status updates after checkout.
			void refreshUserAndTrackSubscriptionSucceeded()
		} catch (error) {
			console.warn("Failed to handle Pixieset upgrade return.", error)
		}
	})
}

const isFileDragEvent = (event: DragEvent): boolean => {
	const dataTransfer = event.dataTransfer
	if (!dataTransfer) {
		return false
	}

	if (dataTransfer.types && Array.from(dataTransfer.types).includes("Files")) {
		return true
	}

	if (dataTransfer.items) {
		return Array.from(dataTransfer.items).some((item) => item.kind === "file")
	}

	return false
}

const isAllowedFileDropTarget = (event: DragEvent): boolean => {
	const path = event.composedPath?.() ?? []
	for (const node of path) {
		if (node instanceof Element && node.hasAttribute("data-allow-file-drop")) {
			return true
		}
	}

	const target = event.target
	if (target instanceof Element) {
		return target.closest("[data-allow-file-drop]") !== null
	}

	return false
}

const shouldBlockFileDrop = (event: DragEvent): boolean => {
	return isFileDragEvent(event) && !isAllowedFileDropTarget(event)
}

const preventGlobalFileDrop = (event: DragEvent): void => {
	if (!isFileDragEvent(event)) {
		return
	}

	event.preventDefault()
	if (event.dataTransfer) {
		event.dataTransfer.dropEffect = "none"
	}
}

const installGlobalFileDropGuard = (): void => {
	const handleDragEnter = (event: DragEvent) => {
		if (!shouldBlockFileDrop(event)) {
			return
		}

		preventGlobalFileDrop(event)
	}

	const handleDragOver = (event: DragEvent) => {
		if (!shouldBlockFileDrop(event)) {
			return
		}

		preventGlobalFileDrop(event)
	}

	const handleDrop = (event: DragEvent) => {
		if (shouldBlockFileDrop(event)) {
			preventGlobalFileDrop(event)
		}
	}

	window.addEventListener("dragenter", handleDragEnter, { capture: true })
	window.addEventListener("dragover", handleDragOver, { capture: true })
	window.addEventListener("drop", handleDrop, { capture: true })
}

const HydrateAtoms: React.FC<React.PropsWithChildren> = ({ children }) => {
	useHydrateAtoms([[queryClientAtom, queryClient]])
	return <>{children ?? null}</>
}

const onRedirectCallback = (appState?: AppState, user?: any): void => {
	analytics.trackEvent({ label: "auth_success", type: "event" })
	if (user?.logins_count === 1) {
		analytics.trackEvent({ label: "signup_succeeded", type: "event" })
	}
	window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname)
}

const skipElectronReauth = (() => {
	if (!isElectronApp) {
		return false
	}

	try {
		const hasAuthCookie = document.cookie.includes(
			`auth0.${auth0ClientId}.is.authenticated=true`,
		)

		if (!hasAuthCookie) {
			return false
		}

		const hasTokenStorage = Object.keys(localStorage).some(
			(key) =>
				key.includes(auth0ClientId) && localStorage.getItem(key)?.includes("refresh_token"),
		)

		if (!hasTokenStorage) {
			return false
		}

		const cookieExpiration = document.cookie
			.split(";")
			.find((c) => c.trim().startsWith(`auth0.${auth0ClientId}.is.authenticated=`))
			?.match(/expires=([^;]+)/)

		if (!cookieExpiration?.[1]) {
			return false
		}

		const cookieExpiresAt = new Date(cookieExpiration[1]).getTime()
		const cookieIsValid = cookieExpiresAt > Date.now() + 60 * 60 * 1000

		return cookieIsValid
	} catch (e) {
		console.error("Error checking auth state:", e)
		return false
	}
})()

const safeInit = (name: string, fn: () => void): void => {
	try {
		fn()
	} catch (error) {
		console.error(`[bootstrap] ${name} failed:`, error)
	}
}

safeInit("sentry", () => {
	if (typeof localStorage !== "undefined") {
		sentryInit()
	}
})
safeInit("amplitude-storage", prepareAmplitudeStorage)
safeInit("maintenance-bypass", applyMaintenanceBypassFromUrl)
safeInit("pixieset-upgrade-return", handlePixiesetUpgradeReturn)
safeInit("pixieset-upgrade-listener", listenForPixiesetUpgradeReturn)
safeInit("file-drop-guard", installGlobalFileDropGuard)
safeInit("theme", () => applyThemeMode(getStoredThemeMode()))

const auth0CacheLocation: "localstorage" | "memory" = (() => {
	try {
		const probeKey = "__polarr_ls_probe__"
		localStorage.setItem(probeKey, "1")
		localStorage.removeItem(probeKey)
		return "localstorage"
	} catch {
		return "memory"
	}
})()

void loadAppConfigForLaunch().then(async (appConfig) => {
	await initI18n(getEnabledLocales(appConfig))

	ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
		<React.StrictMode>
			<ErrorBoundary fallback={({ error }) => <RootErrorView error={error as Error} />}>
				<I18nextProvider i18n={i18n}>
					<MaintenanceModeGate maintenanceStatusUrl={photoEditorMaintenanceStatusUrl}>
						<BrowserCompatibilityGuard>
							<Auth0Provider
								domain={auth0Domain}
								clientId={auth0ClientId}
								authorizationParams={{
									audience: auth0Audience,
									redirect_uri: getRedirectURI({ legacy: true }),
								}}
								onRedirectCallback={onRedirectCallback}
								cacheLocation={auth0CacheLocation}
								useRefreshTokens={true}
								useRefreshTokensFallback={false}
								skipRedirectCallback={skipElectronReauth}
							>
								<QueryClientProvider client={queryClient}>
									<HydrateAtoms>
										<RouterProvider router={router} />
									</HydrateAtoms>
								</QueryClientProvider>
							</Auth0Provider>
						</BrowserCompatibilityGuard>
					</MaintenanceModeGate>
				</I18nextProvider>
			</ErrorBoundary>
		</React.StrictMode>,
	)
})
